import React from "react";
import { Moon, Sunrise, Flame, Brain, PartyPopper, Waves, Shuffle, Mic, Play } from "lucide-react";
import { MOODS, DECADES } from "@/lib/moods";
import { useAppState } from "@/context/AppState";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ListeningStats from "@/components/ListeningStats";

const ICONS = { moon: Moon, sunrise: Sunrise, flame: Flame, brain: Brain, "party-popper": PartyPopper, waves: Waves, shuffle: Shuffle, mic: Mic };

const COLORS = {
  cyan:   { text: "text-cyan-300",  border: "hover:border-cyan-500/60", glow: "hover:shadow-cyan-500/20",  dot: "bg-cyan-400" },
  pink:   { text: "text-pink-300",  border: "hover:border-pink-500/60", glow: "hover:shadow-pink-500/20",  dot: "bg-pink-400" },
  yellow: { text: "text-yellow-300",border: "hover:border-yellow-500/60",glow: "hover:shadow-yellow-500/20",dot: "bg-yellow-400" },
  green:  { text: "text-green-300", border: "hover:border-green-500/60",glow: "hover:shadow-green-500/20", dot: "bg-green-400" },
};

function MoodTile({ mood, onOpen, onPlay, t }) {
  const Icon = ICONS[mood.icon] || Shuffle;
  const c = COLORS[mood.color] || COLORS.cyan;
  return (
    <div
      data-testid={`mood-tile-${mood.key}`}
      onClick={() => onOpen(mood)}
      className={`relative group cursor-pointer border border-[var(--voix-border)] bg-[#080808] p-5 h-40 flex flex-col justify-between overflow-hidden transition-all duration-300 station-card ${c.border} ${c.glow} hover:shadow-2xl`}
    >
      <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-30 blur-2xl ${c.dot}`} />
      <div className="relative flex items-start justify-between">
        <div className={`w-10 h-10 border border-[var(--voix-border)] bg-black/60 flex items-center justify-center ${c.text}`}>
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <button
          data-testid={`mood-play-${mood.key}`}
          onClick={(e) => { e.stopPropagation(); onPlay(mood); }}
          className={`w-9 h-9 rounded-full bg-white/5 hover:bg-cyan-500 hover:text-black transition text-white flex items-center justify-center opacity-0 group-hover:opacity-100`}
          title="Play now"
        >
          <Play size={13} className="translate-x-0.5" />
        </button>
      </div>
      <div className="relative">
        <div className="font-heading font-black text-xl leading-tight tracking-tight text-white">{t(`mood.${mood.key}`)}</div>
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-slate-500 mt-1">{mood.subtitle}</div>
      </div>
    </div>
  );
}

export default function MoodGrid() {
  const { setView, setFilters } = useAppState();
  const { playStation } = usePlayer();
  const { t } = useI18n();

  const openMood = (mood) => {
    setFilters((f) => ({ ...f, mood: mood.key, tag: mood.tag || null, decade: null, country: null, language: null }));
    setView("mood");
  };

  const playMood = async (mood) => {
    try {
      const params = {};
      if (mood.tag) params.tag = mood.tag;
      const station = await api.randomStation(params);
      playStation({ ...station, mood: mood.key, tag: mood.tag });
    } catch {
      toast.error(t("toast.no_random"));
    }
  };

  const openDecade = (decade) => {
    setFilters((f) => ({ ...f, decade: decade.key, tag: decade.tag, mood: null, country: null, language: null }));
    setView("decade");
  };

  const playDecade = async (decade) => {
    try {
      const station = await api.randomStation({ tag: decade.tag });
      playStation(station);
    } catch {
      toast.error(t("toast.no_random_dec"));
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-10">
      <div>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-cyan-400">{t("landing.moods.pre")}</div>
        <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tight mt-1">{t("landing.moods.title")}<span className="text-cyan-400">.</span></h1>
        <p className="font-body text-sm text-slate-500 mt-2 max-w-lg">
          {t("landing.moods.subtitle")}
        </p>
      </div>

      <div data-testid="mood-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {MOODS.map((m) => (
          <MoodTile key={m.key} mood={m} onOpen={openMood} onPlay={playMood} t={t} />
        ))}
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-pink-400">{t("landing.timemachine.pre")}</div>
            <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-1">{t("landing.timemachine.title")}</h2>
          </div>
        </div>
        <div data-testid="decade-grid" className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {DECADES.map((d) => {
            const c = COLORS[d.color] || COLORS.cyan;
            return (
              <div
                key={d.key}
                data-testid={`decade-tile-${d.key}`}
                onClick={() => openDecade(d)}
                className={`station-card group cursor-pointer border border-[var(--voix-border)] bg-[#080808] p-4 h-24 flex items-center justify-between ${c.border}`}
              >
                <div className={`font-heading font-black text-3xl tracking-tight ${c.text}`}>{d.label}</div>
                <button
                  data-testid={`decade-play-${d.key}`}
                  onClick={(e) => { e.stopPropagation(); playDecade(d); }}
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-cyan-500 hover:text-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                >
                  <Play size={12} className="translate-x-0.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Listening stats — personal listening heatmap */}
      <ListeningStats />
    </div>
  );
}
